// Razorpay configuration and payment service
import API_BASE_URL_CONFIG from '../Config/config';
const API_BASE_URL = API_BASE_URL_CONFIG;

// Razorpay key will be fetched from backend API (no hardcoded keys)
const RAZORPAY_CONFIG = {
  keyId: null, // Will be set from backend API response
  keySecret: null // Not used in frontend (only backend needs secret)
};

const API_ENDPOINTS = {
  SUBSCRIPTION: {
    CREATE_ORDER: `${API_BASE_URL}/api/subscription/create-order`,
    STORE_PAYMENT: `${API_BASE_URL}/api/subscription/store-payment`,
    STATUS: (userId) => `${API_BASE_URL}/api/subscription/status/${userId}`,
    VERIFY_PAYMENT: `${API_BASE_URL}/api/subscription/verify-payment`,
    ALL_PAYMENTS: `${API_BASE_URL}/api/subscription/all-payments`
  }
};

export const initializeRazorpay = () => {
  return new Promise((resolve) => {
    // Check if Razorpay is already loaded
    if (window.Razorpay) {
      resolve(true);
      return;
    }

    // Check if script is already being loaded
    const existingScript = document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]');
    if (existingScript) {
      existingScript.onload = () => resolve(true);
      existingScript.onerror = () => resolve(false);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => {
      console.log('Razorpay SDK loaded successfully');
      resolve(true);
    };
    script.onerror = (error) => {
      console.error('Failed to load Razorpay SDK:', error);
      resolve(false);
    };
    document.body.appendChild(script);
  });
};

export const createRazorpayOrder = async (amount, currency = 'INR') => {
  try {
    const userId = localStorage.getItem('user_id');
    console.log('Creating Razorpay order:', { amount, currency, userId });
    
    const response = await fetch(API_ENDPOINTS.SUBSCRIPTION.CREATE_ORDER, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        amount: amount,
        currency: currency,
        user_id: userId
      })
    });

    console.log('Order creation response status:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.warn('Backend order creation failed:', errorText);
      throw new Error('Failed to create order');
    }

    const result = await response.json();
    console.log('Order created successfully:', result.order);
    // Return both order and keyId
    return {
      order: result.order,
      keyId: result.keyId
    };
  } catch (error) {
    console.warn('Error creating order:', error);
    throw error; // Don't use fallback - fail if backend is unavailable
  }
};

export const processPayment = async (amount, userDetails) => {
  try {
    // Initialize Razorpay if not already loaded
    const isLoaded = await initializeRazorpay();
    if (!isLoaded) {
      throw new Error('Razorpay SDK failed to load');
    }

    // Wait a moment for Razorpay to fully initialize
    await new Promise(resolve => setTimeout(resolve, 500));

    // Create order and get keyId from backend
    const orderResult = await createRazorpayOrder(amount);
    const order = orderResult.order;
    const razorpayKey = orderResult.keyId;
    
    console.log('Using order for payment:', order);
    
    if (!razorpayKey) {
      throw new Error('Failed to get Razorpay key from backend');
    }
    
    const options = {
      key: razorpayKey,
      amount: order.amount,
      currency: order.currency,
      name: 'TheSkillGenie',
      description: 'Premium Subscription - 90 Days',
      image: 'https://placehold.co/150x150/000000/FFFFFF?text=TSG', // Fallback image
      order_id: order.id,
      handler: async (response) => {
        // Handle successful payment
        try {
          const userId = localStorage.getItem('user_id');
          
          // Store payment in database
          const paymentResponse = await fetch(API_ENDPOINTS.SUBSCRIPTION.STORE_PAYMENT, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              user_id: userId,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
              amount: amount,
              payment_method: 'razorpay',
              status: 'success'
            })
          });

          if (!paymentResponse.ok) {
            console.error('Failed to store payment in database');
            // Continue with local storage as fallback
          }

          // Store subscription data locally as well
          const subscriptionData = {
            isSubscribed: true,
            subscriptionStart: new Date().toISOString(),
            subscriptionEnd: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString(),
            amount: amount,
            paymentId: response.razorpay_payment_id,
            orderId: response.razorpay_order_id,
            paymentMethod: 'razorpay',
            status: 'active'
          };

          localStorage.setItem(`subscription_data_${userId}`, JSON.stringify(subscriptionData));
          
          // Redirect to success page
          window.location.href = '/payment-success';
          
          return {
            success: true,
            paymentId: response.razorpay_payment_id,
            orderId: response.razorpay_order_id,
            subscriptionData
          };
        } catch (error) {
          console.error('Payment verification failed:', error);
          throw error;
        }
      },
      prefill: {
        name: userDetails.name || 'User',
        email: userDetails.email || 'user@example.com',
        contact: userDetails.phone || '9999999999'
      },
      notes: {
        address: 'TheSkillGenie Premium Subscription'
      },
      theme: {
        color: '#F59E0B' // Yellow color matching your theme
      },
      config: {
        display: {
          hide: ['method.card.upi']
        }
      },
      modal: {
        ondismiss: () => {
          // Handle modal dismissal
          console.log('Payment modal dismissed');
        }
      }
    };

    // Open Razorpay checkout
    try {
      console.log('Opening Razorpay checkout with options:', options);
      const razorpay = new window.Razorpay(options);
      razorpay.open();

      return new Promise((resolve, reject) => {
        razorpay.on('payment.success', (response) => {
          console.log('Payment successful:', response);
          resolve({
            success: true,
            paymentId: response.razorpay_payment_id,
            orderId: response.razorpay_order_id
          });
        });

        razorpay.on('payment.error', (error) => {
          console.error('Payment error:', error);
          // Don't throw error, just log it and resolve with failure
          resolve({
            success: false,
            error: error.description || 'Payment failed'
          });
        });

        razorpay.on('payment.cancel', () => {
          console.log('Payment cancelled by user');
          resolve({
            success: false,
            error: 'Payment cancelled by user'
          });
        });
      });
    } catch (error) {
      console.error('Failed to initialize Razorpay checkout:', error);
      throw new Error('Failed to initialize payment checkout');
    }

  } catch (error) {
    console.error('Payment processing error:', error);
    throw error;
  }
};

export const verifyPayment = async (paymentData) => {
  try {
    // In a real application, you would verify the payment signature on your backend
    // For now, we'll simulate successful verification
    const response = await fetch('/api/verify-payment', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(paymentData)
    }).catch(() => {
      // If backend is not available, simulate verification
      return {
        ok: true,
        json: () => Promise.resolve({
          verified: true,
          message: 'Payment verified successfully'
        })
      };
    });

    const verification = await response.json();
    return verification;
  } catch (error) {
    console.error('Payment verification error:', error);
    throw error;
  }
};

export default {
  initializeRazorpay,
  createRazorpayOrder,
  processPayment,
  verifyPayment,
  config: RAZORPAY_CONFIG
};



